/* src/modules/office/components/TeamScheduleManager.js */
import React, { useState, useEffect } from 'react';
import { 
    collection, query, where, getDocs, addDoc, updateDoc, 
    deleteDoc, doc, serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { 
    PlusIcon, PencilIcon, TrashIcon 
} from '../../../components/icons/Icons';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

// Helper to format pattern string (e.g., "O-O-H-H-O")
const formatPatternString = (pattern) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    return days.map(d => pattern[d] === 'WFO' ? 'O' : 'H').join('-');
};

// ✅ FIX: Robust Date Formatter
// Handles Firestore Timestamps, JS Dates, and ISO strings
const formatDate = (val) => {
    if (!val) return '-';
    
    // 1. Handle Firestore Timestamp (has .toDate())
    if (val && typeof val.toDate === 'function') {
        val = val.toDate();
    }
    
    // 2. Handle standard Date object or string
    const d = new Date(val);
    
    // Check if invalid
    if (isNaN(d.getTime())) return '-';
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
};

export default function TeamScheduleManager({ userProfile, appId }) {
    const [schedules, setSchedules] = useState([]);
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        startDate: '',
        endDate: '',
        pattern: { monday: 'WFO', tuesday: 'WFO', wednesday: 'WFO', thursday: 'WFH', friday: 'WFH' },
        assignedUsers: []
    });
    const [isSaving, setIsSaving] = useState(false);

    // 1. Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // A. Fetch Team Members (Active Only)
                const qUsers = query(
                    collection(db, `artifacts/${appId}/users`), 
                    where("managerId", "==", userProfile.id),
                    where("status", "==", "Approved") 
                );
                const userSnap = await getDocs(qUsers);
                const members = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                
                // Add Manager (Self)
                members.unshift({ id: userProfile.id, name: `${userProfile.name} (Me)`, photoURL: userProfile.photoURL });
                setTeam(members);

                // B. Fetch Schedules
                const qSched = query(
                    collection(db, `artifacts/${appId}/office_schedules`), 
                    where("managerId", "==", userProfile.id)
                );
                const schedSnap = await getDocs(qSched);
                const fetchedSchedules = schedSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                
                // Sort by UpdatedAt (most recent first)
                fetchedSchedules.sort((a, b) => {
                    const dateA = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
                    const dateB = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
                    return dateB - dateA; 
                });

                setSchedules(fetchedSchedules);

            } catch (e) {
                console.error("Error loading team data:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [appId, userProfile]);

    // 2. Open Modal
    const handleOpenModal = (schedule = null) => {
        if (schedule) {
            setEditingSchedule(schedule);
            setFormData({
                title: schedule.title || 'Team Schedule',
                startDate: schedule.startDate,
                endDate: schedule.endDate,
                pattern: schedule.pattern,
                assignedUsers: schedule.assignedUsers || []
            });
        } else {
            setEditingSchedule(null);
            setFormData({
                title: '',
                startDate: '',
                endDate: '',
                pattern: { monday: 'WFO', tuesday: 'WFO', wednesday: 'WFO', thursday: 'WFH', friday: 'WFH' },
                assignedUsers: []
            });
        }
        setIsModalOpen(true);
    };

    // 3. Validation
    const validateOverlap = () => {
        const newStart = formData.startDate;
        const newEnd = formData.endDate;
        const otherSchedules = schedules.filter(s => !editingSchedule || s.id !== editingSchedule.id);

        for (const user of formData.assignedUsers) {
            for (const sched of otherSchedules) {
                if (sched.assignedUsers.includes(user)) {
                    if (newStart <= sched.endDate && newEnd >= sched.startDate) {
                        const userName = team.find(u => u.id === user)?.name || 'Unknown User';
                        alert(`Conflict Detected!\n\nUser: ${userName}\nOverlap with: "${sched.title}"\nDates: ${sched.startDate} to ${sched.endDate}`);
                        return false;
                    }
                }
            }
        }
        return true;
    };

    // 4. Save Logic (Fixed Date Handling)
    const handleSave = async (e) => {
        e.preventDefault();
        if (formData.assignedUsers.length === 0) return alert("Please select at least one team member.");
        if (!validateOverlap()) return;

        setIsSaving(true);
        try {
            // Payload for Firestore
            const payload = {
                managerId: userProfile.id,
                title: formData.title || 'Untitled Schedule',
                startDate: formData.startDate,
                endDate: formData.endDate,
                pattern: formData.pattern,
                assignedUsers: formData.assignedUsers,
                updatedAt: serverTimestamp() // Firestore Sentinel
            };

            const now = new Date(); // Local Date Object

            if (editingSchedule) {
                await updateDoc(doc(db, `artifacts/${appId}/office_schedules`, editingSchedule.id), payload);
                
                // Update Local State with JS Date (Not Sentinel)
                setSchedules(prev => prev.map(s => s.id === editingSchedule.id ? { 
                    ...s, 
                    ...payload, 
                    updatedAt: now 
                } : s));
            } else {
                const docRef = await addDoc(collection(db, `artifacts/${appId}/office_schedules`), {
                    ...payload,
                    createdAt: serverTimestamp()
                });
                
                // Add to Local State with JS Date (Not Sentinel)
                setSchedules([{ 
                    id: docRef.id, 
                    ...payload, 
                    createdAt: now,
                    updatedAt: now // ✅ Explicitly setting this fixes the display issue on create
                }, ...schedules]);
            }
            setIsModalOpen(false);
        } catch (e) {
            console.error(e);
            alert("Error saving schedule.");
        } finally {
            setIsSaving(false);
        }
    };

    // 5. Delete Logic
    const handleDelete = async (id) => {
        if (!window.confirm("⚠️ Warning: Deleting this schedule will remove the assigned work pattern for these employees.\n\nPast attendance reports relying on this schedule will revert to 'Unassigned'.\n\nTo preserve history, edit the 'End Date' instead.\n\nAre you sure you want to delete?")) return;
        
        try {
            await deleteDoc(doc(db, `artifacts/${appId}/office_schedules`, id));
            setSchedules(prev => prev.filter(s => s.id !== id));
        } catch (e) {
            console.error(e);
            alert("Failed to delete.");
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            
            {/* Header */}
            <div className="flex justify-between items-center bg-blue-50 p-4 rounded-lg border border-blue-100">
                <div>
                    <h3 className="text-lg font-bold text-[#213368]">Manage Team Schedules</h3>
                    <p className="text-sm text-gray-500">Define work patterns for different groups.</p>
                </div>
                <button 
                    onClick={() => handleOpenModal()} 
                    className="flex items-center px-4 py-2 bg-[#20BEC6] text-white rounded-lg font-bold hover:opacity-90 shadow-sm transition"
                >
                    <PlusIcon className="h-5 w-5 mr-1" /> Add Schedule
                </button>
            </div>

            {/* List Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                        <tr>
                            <th className="px-6 py-3 text-left">Schedule Name</th>
                            <th className="px-6 py-3 text-left">Start Date</th>
                            <th className="px-6 py-3 text-left">End Date</th>
                            <th className="px-6 py-3 text-left" title="Mon-Tue-Wed-Thu-Fri (O=Office, H=Home)">Pattern (M-F)</th>
                            <th className="px-6 py-3 text-left">Last Edited</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {schedules.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-8 text-center text-gray-400">No active schedules found.</td>
                            </tr>
                        ) : (
                            schedules.map(schedule => (
                                <tr key={schedule.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-bold text-[#213368]">{schedule.title}</div>
                                        <div className="text-xs text-gray-500">{schedule.assignedUsers?.length || 0} Members</div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                                        {schedule.startDate}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                                        {schedule.endDate}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="bg-gray-100 text-gray-600 font-mono text-xs px-2 py-1 rounded border border-gray-200 tracking-widest">
                                            {formatPatternString(schedule.pattern)}
                                        </span>
                                    </td>
                                    {/* ✅ Uses updated logic to handle both Timestamps and Dates */}
                                    <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                                        {formatDate(schedule.updatedAt || schedule.createdAt)}
                                    </td>
                                    <td className="px-6 py-4 text-right space-x-2">
                                        <button onClick={() => handleOpenModal(schedule)} className="text-blue-600 hover:text-blue-800 p-1" title="Edit">
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => handleDelete(schedule.id)} className="text-gray-400 hover:text-red-600 p-1" title="Delete">
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-5 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                            <h3 className="text-xl font-bold text-[#213368]">{editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 font-bold text-xl">&times;</button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto">
                            <form onSubmit={handleSave} className="space-y-6">
                                {/* Basic Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="col-span-full">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Schedule Name</label>
                                        <input type="text" className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" placeholder="e.g. Sales Team - Q1 Plan" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Start Date</label>
                                        <input type="date" className="w-full p-2 border rounded" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} required />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">End Date</label>
                                        <input type="date" className="w-full p-2 border rounded" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} required />
                                    </div>
                                </div>

                                {/* Pattern */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Weekly Pattern</label>
                                    <div className="flex justify-between space-x-2 bg-gray-50 p-3 rounded border">
                                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => (
                                            <div key={day} className="flex flex-col items-center w-full">
                                                <span className="capitalize text-[10px] font-bold text-gray-500 mb-1">{day.slice(0,3)}</span>
                                                <button 
                                                    type="button" 
                                                    onClick={() => setFormData(prev => ({ 
                                                        ...prev, 
                                                        pattern: { ...prev.pattern, [day]: prev.pattern[day] === 'WFO' ? 'WFH' : 'WFO' } 
                                                    }))}
                                                    className={`w-full py-2 text-xs font-bold rounded transition border ${
                                                        formData.pattern[day] === 'WFO' 
                                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm' 
                                                        : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    {formData.pattern[day] === 'WFO' ? 'O' : 'H'}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* User Selection */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-xs font-bold text-gray-500 uppercase">Assign Members</label>
                                        <button 
                                            type="button" 
                                            className="text-xs text-blue-600 font-bold hover:underline"
                                            onClick={() => setFormData({...formData, assignedUsers: formData.assignedUsers.length === team.length ? [] : team.map(u => u.id)})}
                                        >
                                            {formData.assignedUsers.length === team.length ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>
                                    <div className="border rounded-lg max-h-48 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-gray-50">
                                        {team.map(user => (
                                            <label key={user.id} className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition bg-white border ${formData.assignedUsers.includes(user.id) ? 'border-blue-300 shadow-sm ring-1 ring-blue-100' : 'border-gray-200'}`}>
                                                <input 
                                                    type="checkbox" 
                                                    className="rounded text-[#20BEC6] focus:ring-[#20BEC6]"
                                                    checked={formData.assignedUsers.includes(user.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setFormData(prev => ({ ...prev, assignedUsers: [...prev.assignedUsers, user.id] }));
                                                        else setFormData(prev => ({ ...prev, assignedUsers: prev.assignedUsers.filter(id => id !== user.id) }));
                                                    }}
                                                />
                                                <span className={`text-sm truncate ${formData.assignedUsers.includes(user.id) ? 'font-bold text-[#213368]' : 'text-gray-600'}`}>{user.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                            <button onClick={() => setIsModalOpen(false)} className="px-5 py-2 text-gray-600 hover:bg-gray-200 rounded-lg font-medium transition">Cancel</button>
                            <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-[#20BEC6] text-white font-bold rounded-lg hover:opacity-90 shadow-sm transition">
                                {isSaving ? 'Saving...' : 'Save Schedule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}